import Checkbox from '@allesauto/core/checkbox';
import RadioButton from '@allesauto/core/radioButton';
import Slider from '@allesauto/core/slider';
import { PowerType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { getSlug } from '@allesauto/utils/funcs';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DriveStateProps } from '@/store/redux/reducers/pages/pcp/filters/drive';

export interface DriveFilterProps {
  gearBoxList: SchemaTypes.DriveFacet['gearBox'];
  powerList: SchemaTypes.DriveFacet['power'];
  minCapacity: number;
  maxCapacity: number;
  rangeLabelText: string;
  onChange: (state: DriveStateProps) => void;
  value: DriveStateProps;
  darkBg?: boolean;
}

const DriveFilter: React.FC<React.PropsWithChildren<DriveFilterProps>> = ({
  gearBoxList,
  powerList,
  minCapacity,
  maxCapacity,
  rangeLabelText,
  onChange,
  value,
  darkBg,
}) => {
  const intl = useIntl();

  const trackingIdentifierFuel = 'aa-filter--power';
  const trackingIdentifierPowerGearbox = `${trackingIdentifierFuel}-gearbox`;
  const trackingIdentifierPowerCapacity = `${trackingIdentifierFuel}-capacity`;
  const trackingIdentifierPowerRange = `${trackingIdentifierFuel}-range`;
  const trackingIdentifierPowerType = `${trackingIdentifierFuel}-type`;

  const { gearBox, power, maxCubicCapacity } = value;

  const powerTypes = powerList?.map((item) => item?.key) ?? [];

  const { powerMin, powerMax } = useMemo(() => {
    const powerRange = powerList?.find((i) => i && i.key === power.key);

    return {
      powerMin: powerRange ? powerRange.min : 0,
      powerMax: powerRange ? powerRange.max : 0,
    };
  }, [powerList, power.key]);

  const handleCheckboxChange = useCallback(
    (item: string, checked: boolean) => {
      const { gearBox } = value;
      let newGearBoxValues = [];

      if (checked) {
        newGearBoxValues = [...gearBox, item];
      } else {
        newGearBoxValues = [
          ...gearBox.slice(0, gearBox.indexOf(item)),
          ...gearBox.slice(gearBox.indexOf(item) + 1),
        ];
      }

      onChange({ ...value, gearBox: newGearBoxValues });
    },
    [value, onChange]
  );

  const handlePowerTypeChange = (powerType: string) => {
    const key = powerType as PowerType;
    const powerRange = powerList?.find((i) => i && i.key === key);

    onChange({
      ...value,
      power: {
        key,
        min: powerRange?.min ?? power.min,
        max: powerRange?.max ?? power.max,
      },
    });
  };

  const handlePowerRangeChange = (rangeValue: number | number[]) => {
    let min, max;

    if (typeof rangeValue === 'number') {
      min = max = rangeValue;
    } else {
      min = rangeValue[0];
      max = rangeValue[1];
    }

    onChange({
      ...value,
      power: { ...power, min, max },
    });
  };

  const handleMaxCubicCapacityChange = (capacity: number | number[]) => {
    onChange({
      ...value,
      maxCubicCapacity: Array.isArray(capacity) ? capacity[0] : capacity,
    });
  };

  const className = {
    text: 'font-regular font-weight-normal text-p1 text-white',
    checkboxes: 'flex flex-col pt-30px',
    checkbox: 'mb-20px last:mb-0',
    radio: 'flex gap-30px mb-20px',
    block: 'mb-40px w-full',
    slider: 'mt-10px sm:w-[98%]',
  };

  const { text, block, checkboxes, checkbox, radio, slider } = className;

  return (
    <div>
      <div className={block} data-tracking-id={trackingIdentifierPowerGearbox}>
        <div className={text}>
          {translate(intl, 'ui.PCP.FILTERS.DRIVE.GEAR_BOX_LABEL')}
        </div>
        {gearBoxList && (
          <div className={checkboxes}>
            {gearBoxList.map(
              (item) =>
                item && (
                  <Checkbox
                    id={`${trackingIdentifierPowerGearbox}-${getSlug(
                      item.name
                    )}`}
                    className={checkbox}
                    key={item.name}
                    label={item.displayName}
                    checked={gearBox.includes(item.name)}
                    onChange={(v) => handleCheckboxChange(item.name, v)}
                    darkBg={darkBg}
                  />
                )
            )}
          </div>
        )}
      </div>
      <div className={block} data-tracking-id={trackingIdentifierPowerType}>
        <div className={radio}>
          <div className={text}>
            {translate(intl, 'ui.PCP.FILTERS.DRIVE.POWER_LABEL')}
          </div>
          {powerTypes.map(
            (powerType) =>
              powerType && (
                <RadioButton
                  id={`${trackingIdentifierPowerType}-${getSlug(powerType)}`}
                  key={powerType}
                  label={translate(intl, `ui.PCP.FILTERS.DRIVE.${powerType}`)}
                  name="powerType"
                  value={powerType}
                  checked={power.key === powerType}
                  onChange={handlePowerTypeChange}
                  darkBg={darkBg}
                />
              )
          )}
        </div>
        <Slider
          id={trackingIdentifierPowerRange}
          rangeProps={{
            onChange: handlePowerRangeChange,
          }}
          step={10}
          range={true}
          min={powerMin}
          max={powerMax}
          value={[power.min ?? powerMin, power.max ?? powerMax]}
          rangeLabelText={rangeLabelText}
          darkBg={darkBg}
        />
      </div>
      <div
        className={slider}
        data-tracking-id={trackingIdentifierPowerCapacity}
      >
        <div className={text}>
          {translate(intl, 'ui.PCP.FILTERS.DRIVE.CAPACITY_LABEL')}
        </div>
        <Slider
          id={trackingIdentifierPowerCapacity}
          step={500}
          onChange={handleMaxCubicCapacityChange}
          min={minCapacity}
          max={maxCapacity}
          value={[maxCubicCapacity ?? maxCapacity]}
          darkBg={darkBg}
        />
      </div>
    </div>
  );
};

export default DriveFilter;
